import ModalService from '@/helpers/modal.service';
import UploadService from '@/helpers/upload-service';
import MediaRepository from '@/repositories/media-repository';
import useJobsStore from '@/store/jobs';
import useUploadStore from '@/store/upload';
import { Job, Job$Status, Job$Type } from '@/types/Job';
import { Media$Details, Media$Version, Media$Quality, Media$Type } from '@/types/Media';
import { UploadInfo, UploadStatus } from '@/types/Upload';
import { Watch } from '@Ravnur-Inc/decorators';
import flow from '@Ravnur-Inc/nanoutils/flow';
import groupBy from '@Ravnur-Inc/nanoutils/groupBy';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { Vue, Options, setup } from 'vue-class-component';

import VideoQualitiesTable from '../media-qualities-table/media-qualities-table';

import VideoVersionsTable from '../media-versions-table/media-versions-table';

import './media-versions.scss';

const CN = 'media-versions';
const repository = new MediaRepository();

const prepareQualities = flow(
  sortBy((q: Media$Quality) => +q.quality),
  reverse,
  groupBy('type')
);

const ACCEPT_BY_TYPES: { [key in Media$Type]?: string } = {
  [Media$Type.VIDEO]: 'video/*',
  [Media$Type.AUDIO]: 'audio/*',
};

class Props {
  origin!: Media$Details;
}

@Options({
  inject: {
    uploadService: 'UPLOAD_SERVICE',
  },
})
export default class MediaVersions extends Vue.with(Props) {
  declare uploadService: UploadService;

  private jobs = setup(() => useJobsStore());
  private uploadStore = setup(() => useUploadStore());
  private loading = false;
  private versions: Media$Version[] = [];
  private qualities: Media$Quality[] = [];

  get videos(): Media$Quality[] {
    return this.preparedQualities.video || [];
  }

  get audios(): Media$Quality[] {
    return this.preparedQualities.audio || [];
  }

  get preparedQualities() {
    return prepareQualities(this.qualities);
  }

  get mediaId() {
    return this.origin.id;
  }

  get job(): Nullable<Job> {
    return this.jobs.all[this.mediaId];
  }

  get uploadInfo(): Nullable<UploadInfo> {
    return this.uploadStore.cacheByMediaId ? this.uploadStore.cacheByMediaId[this.mediaId] : null;
  }

  get disabled() {
    return (
      !!this.job ||
      (this.uploadInfo && this.uploadInfo?.status !== UploadStatus.FINISHED) ||
      this.versions.some((v) => !!v.job && v.job?.state !== Job$Status.SUCCESS)
    );
  }

  get accept() {
    const {
      origin: { type },
    } = this;
    return ACCEPT_BY_TYPES[type] || '*/*';
  }

  @Watch('mediaId', { immediate: true })
  protected handleMediaChanged() {
    this.load();
  }

  @Watch('uploadInfo.status')
  protected handleUploadFinished(newValue: string) {
    if (newValue === UploadStatus.FINISHED) {
      this.load();
      this.uploadStore.stopByMediaId(this.mediaId);
    }

    if (newValue === UploadStatus.FAILED) {
      showErrorNotification(
        this.$t('common', 'notification_center__failed_new-version', { mediaId: this.mediaId })
      );
    }
  }

  @Watch('job', { immediate: true, deep: true })
  protected handleJobChanged(job: Nullable<Job>, old: Nullable<Job>) {
    if (old && old.type === Job$Type.TRANSCODING && this.versions.length === 1) {
      return;
    }

    if (!job) {
      this.load();
      return;
    }

    const { versions } = this;
    versions.forEach((v) => {
      if (v.job && v.job.id === job.id) {
        v.job = job;
      }
    });
  }

  render() {
    return (
      <div class={CN}>
        <div class={`${CN}__header`}>
          <l10n
            class={[`${CN}__block-title`, `${CN}__block-title--versions`]}
            group="media"
            tkey="download__versions_title"
          />

          <input
            ref="upload"
            accept={this.accept}
            class={`${CN}__hidden`}
            type="file"
            onInput={this.handleFileSelected}
          />

          <r-button
            class={`${CN}__upload-btn`}
            color="primary"
            disabled={this.disabled}
            icon="upload"
            onclick={this.handleUploadClick}
            v-show={this.videos.length}
          >
            <l10n group="media" tkey="download__upload-new-version" />
          </r-button>
        </div>

        <VideoVersionsTable
          list={this.versions}
          media={this.origin}
          onRefreshVersions={this.load}
        />

        <l10n
          class={`${CN}__block-title`}
          group="media"
          tkey="download__video_title"
          v-show={this.videos.length}
        />
        <VideoQualitiesTable list={this.videos} v-show={this.videos.length} />

        <l10n class={`${CN}__block-title`} group="media" tkey="download__audio_title" />
        <VideoQualitiesTable list={this.audios} />

        {this.loading ? <spinner /> : null}
      </div>
    );
  }

  private async load() {
    this.loading = true;

    try {
      const { qualities, versions } = await repository.versions(this.origin);
      this.qualities = qualities;
      this.versions = versions;
    } catch (e) {
      this.$logger.error(e);
    }

    this.loading = false;
  }

  private async confirmUpload(): Promise<{ keepSubResources?: boolean }> {
    const confirmText = this.$t('media', 'download__upload-new-version');
    const msg = this.$t('media', 'download__upload-confirmer');
    const flag = await ModalService.confirm(msg, { confirmText }).catch(() => false);
    return flag ? {} : Promise.reject(new Error(''));
  }

  private async handleUploadClick() {
    let params: { keepSubResources?: boolean } = {};

    try {
      params = await this.confirmUpload();
    } catch (e) {
      return;
    }
    const { upload } = this.$refs;
    if (upload && upload instanceof HTMLInputElement) {
      const { keepSubResources } = params;
      upload.dataset.keepSubResources =
        typeof keepSubResources === 'boolean' ? `${keepSubResources}` : 'false';
      upload.click();
    }
  }

  private handleFileSelected() {
    const { upload } = this.$refs;
    if (!(upload && upload instanceof HTMLInputElement)) {
      return;
    }
    const { files } = upload;
    if (!files) {
      return;
    }
    const file = files.item(0);
    if (!file) {
      return;
    }
    const keepSubResources = upload.dataset.keepSubResources === 'true';
    const mediaId = this.mediaId;
    const autoStartProcess = true;
    this.uploadService.add(file, { keepSubResources, mediaId, autoStartProcess });
    upload.value = '';
  }
}

function reverse<T>(arr: T[]): T[] {
  return [...arr].reverse();
}
